import React from 'react';
import { PointExpirePreviewContainerStyled } from './styled';
import { withRouter } from 'navigations/withRouter';
import { Buttons, Displays, Inputs, Modals, Tables } from 'components';
import { scrollToTopPageFN } from 'utils/functions/scrollTopPage';
import { portalService } from 'apiServices';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import moment from 'moment';
import 'moment-timezone';
import { MdInfoOutline, MdTimerOff, MdWarningAmber } from 'react-icons/md';
import {
  parseFilterQuery,
  syncFilterUrlQuery,
} from 'utils/functions/filterQuery';
import {
  TIME_ZONE,
  formatDateTime,
  formatPoint,
  PERIOD_OPTIONS,
  RENDER_PERIOD_STATE,
} from 'utils/functions/pointExpire';
import { CustomerPointBreakdownContainer } from '../Modals';

const FILTER_FIELDS = [
  'period_key',
  'user_id',
  'customer_cu_id',
  'only_expiring',
  'mismatch_only',
];

// แปลงค่าจาก url เป็น state ของ filter (only_expiring ติ๊กไว้เป็นค่าเริ่มต้น)
const filterFromUrl = (search) => {
  const { filterValues } = parseFilterQuery(search, FILTER_FIELDS);
  return {
    periodKey: filterValues.period_key || '',
    userId: filterValues.user_id || '',
    cuId: filterValues.customer_cu_id || '',
    onlyExpiring: filterValues.only_expiring !== 'false',
    mismatchOnly: filterValues.mismatch_only === 'true',
  };
};

// หน้านี้เป็นการ "จำลองคำนวณ" เท่านั้น ตั้งใจไม่มีปุ่มรันตัดแต้มใน portal
// การตัดจริงทำโดย cron 00:05 น. หรือ script บน server เท่านั้น
class PointExpirePreviewContainer extends React.Component {
  state = {
    isLoading: true,
    isBgLoading: false,
    periods: [],
    data: null,
    page: 1,
    perPage: 10,
    renderModal: false,
    ...filterFromUrl(this.props.router.location.search),
  };

  componentDidMount() {
    scrollToTopPageFN();
    this.fetchPeriods();
    this.fetchPreview();
  }

  fetchPeriods = async () => {
    let res = await portalService.GET_POINT_EXPIRE_PERIODS();
    if (res && res.status === 200) {
      this.setState({ periods: res.data.periods || [] });
    }
  };

  buildQuery = () => {
    const { periodKey, userId, cuId, onlyExpiring, mismatchOnly } = this.state;
    const searchParams = new URLSearchParams();
    if (periodKey) searchParams.set('period_key', periodKey);
    if (userId.trim()) searchParams.set('user_id', userId.trim());
    if (cuId.trim()) searchParams.set('customer_cu_id', cuId.trim());
    if (!onlyExpiring) searchParams.set('only_expiring', 'false');
    if (mismatchOnly) searchParams.set('mismatch_only', 'true');
    const query = searchParams.toString();
    return query ? `?${query}` : '';
  };

  // กัน race: ถ้าผู้ใช้เปลี่ยน filter ซ้อนกันเร็ว ๆ ให้ใช้เฉพาะ response ของคำขอล่าสุด
  // ไม่งั้น response ที่มาช้าจะทับ data และ periodKey ของคำขอใหม่
  reqSeq = 0;

  fetchPreview = async () => {
    const query = this.buildQuery();
    const seq = ++this.reqSeq;
    this.setState({ isLoading: true });
    syncFilterUrlQuery(this.props.router, query, 1);
    let res = await portalService.GET_POINT_EXPIRE_PREVIEW(query);
    if (seq !== this.reqSeq) return;
    if (res && res.status === 200) {
      this.setState({
        data: res.data,
        // ไม่ได้เลือกล็อตมา API จะเลือกล็อตเริ่มต้นให้ เอามาตั้งใน dropdown
        periodKey: (res.data.period && res.data.period.key) || '',
        page: 1,
        isLoading: false,
      });
    } else {
      const message =
        (res && res.data && res.data.error && res.data.error.message) ||
        'โหลดข้อมูลไม่สำเร็จ';
      toast.error(message);
      this.setState({ data: null, isLoading: false });
    }
  };

  handleChangePeriod = (value) => {
    this.setState({ periodKey: value }, this.fetchPreview);
  };

  handleToggle = (key) => {
    this.setState(
      (prevState) => ({ [key]: !prevState[key] }),
      this.fetchPreview
    );
  };

  handleKeyDown = (e) => {
    if (e.key === 'Enter') this.fetchPreview();
  };

  handleClear = () => {
    this.setState(
      {
        periodKey: '',
        userId: '',
        cuId: '',
        onlyExpiring: true,
        mismatchOnly: false,
      },
      this.fetchPreview
    );
  };

  handlePageClick = (e) => {
    this.setState({ page: e.selected + 1 });
  };

  handleCloseModal = () => {
    this.setState({ renderModal: false });
  };

  handleClickBreakdown = (row) => {
    this.setState({
      renderModal: (
        <CustomerPointBreakdownContainer
          customerId={row.customer_ref_id}
          onClose={this.handleCloseModal}
        />
      ),
    });
  };

  // Export เฉพาะผลที่กรองอยู่ (API preview ไม่แบ่งหน้า จึงมีครบทุกแถวใน state แล้ว)
  handleExport = () => {
    const { data } = this.state;
    if (!data || !data.docs || data.docs.length === 0) {
      toast.info('ไม่มีข้อมูลให้ export');
      return;
    }
    this.setState({ isBgLoading: true });
    var wb = XLSX.utils.book_new();
    var ws_name = 'SheetJS';
    var ws_data = data.docs.map((e, i) => [
      i + 1,
      `${e.customer_user_id || ''}`,
      `${e.customer_cu_id || ''}`,
      `${e.firstname || ''} ${e.lastname || ''}`.trim(),
      e.period_key,
      formatPoint(e.lot_received),
      formatPoint(e.lot_redeemed),
      formatPoint(e.lot_expired_before),
      formatPoint(e.lot_remaining),
      formatPoint(e.wallet_point),
      formatPoint(e.ledger_balance),
      formatPoint(e.expired_point),
      e.is_mismatch ? 'ใช่' : '',
      e.mismatch_reason || '',
      Number(e.entry_count || 0),
    ]);
    ws_data.splice(0, 0, [
      'No',
      'Login ID',
      'CU code',
      'ชื่อ',
      'ล็อต',
      'ได้รับในล็อต',
      'ใช้ไปจากล็อต',
      'ตัดหมดอายุก่อนหน้า',
      'คงเหลือในล็อต',
      'ยอดใน wallet',
      'ยอดตาม ledger',
      'ยอดที่จะตัด',
      'ยอดไม่ตรง',
      'สาเหตุยอดไม่ตรง',
      'จำนวนรายการ',
    ]);
    var ws = XLSX.utils.aoa_to_sheet(ws_data);
    ws['!cols'] = [
      { wch: 5 },
      { wch: 30 },
      { wch: 15 },
      { wch: 30 },
      { wch: 10 },
      { wch: 14 },
      { wch: 14 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 10 },
      { wch: 50 },
      { wch: 12 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, ws_name);
    XLSX.writeFile(
      wb,
      `Point expire preview ${data.period ? data.period.key : ''} ${moment()
        .tz(TIME_ZONE)
        .format('DD-MM-YYYY')}.xlsx`
    );
    this.setState({ isBgLoading: false });
  };

  renderSummaryCard = (label, value, className) => (
    <div className={`summary_card ${className || ''}`}>
      <div className="summary_label">{label}</div>
      <div className="summary_value">{value}</div>
    </div>
  );

  render() {
    const {
      isLoading,
      isBgLoading,
      periods,
      data,
      page,
      perPage,
      periodKey,
      userId,
      cuId,
      onlyExpiring,
      mismatchOnly,
      renderModal,
    } = this.state;
    const docs = (data && data.docs) || [];
    const summary = (data && data.summary) || {};
    const period = data && data.period;
    // สถานะการรันของล็อตนี้มาจาก /periods (preview ไม่ได้ส่งมา)
    const periodMeta =
      period && periods.find((p) => p.key === period.key)
        ? { ...periods.find((p) => p.key === period.key), ...period }
        : period;
    const periodOptions = PERIOD_OPTIONS(periods);
    const totalPages = Math.max(1, Math.ceil(docs.length / perPage));
    const pageDocs = docs.slice((page - 1) * perPage, page * perPage);

    return (
      <PointExpirePreviewContainerStyled>
        <Displays.BGLoading visible={isBgLoading} />
        <div className="info_card">
          <div className="info_icon">
            <MdTimerOff size={24} />
          </div>
          <div className="info_body">
            <div className="info_title">จำลองการตัดแต้มหมดอายุ (Preview)</div>
            <div className="info_desc">
              แต้มที่อนุมัติช่วง 1 ม.ค. – 30 มิ.ย. หมดอายุสิ้นวัน 31 ธ.ค. ปีเดียวกัน /
              แต้มที่อนุมัติช่วง 1 ก.ค. – 31 ธ.ค. หมดอายุสิ้นวัน 30 มิ.ย. ปีถัดไป
              (ล็อตเรียกตาม period_key เช่น 2026H1) แต้มที่ได้ก่อน 1 ม.ค. 2569
              เป็นล็อต "legacy" ไม่หมดอายุ ตอนแลกของรางวัลระบบจะหักจากล็อตที่หมดอายุเร็วสุดก่อน
            </div>
            <div className="info_note">
              <MdInfoOutline size={16} />
              หน้านี้เป็นการคำนวณล่วงหน้าเท่านั้น ไม่มีปุ่มรันตัดแต้มในหน้านี้
              ระบบจะตัดให้อัตโนมัติเวลา 00:05 น. ของวันถัดจากวันหมดอายุ
              ผลการตัดดูได้ที่แท็บ Expire history / Run history
            </div>
          </div>
        </div>

        <div className="filter_layout">
          <div className="filter_item period_item">
            <Inputs.Dropdown
              label="ล็อต : "
              placeholder="เลือกล็อต"
              options={periodOptions}
              value={periodKey}
              onChange={this.handleChangePeriod}
            />
          </div>
          <div className="filter_item">
            <Inputs.TextField
              label="Login ID : "
              placeholder="Login ID"
              value={userId}
              onChange={(e) => this.setState({ userId: e.target.value })}
              onKeyDown={this.handleKeyDown}
            />
          </div>
          <div className="filter_item">
            <Inputs.TextField
              label="CU code : "
              placeholder="CU code"
              value={cuId}
              onChange={(e) => this.setState({ cuId: e.target.value })}
              onKeyDown={this.handleKeyDown}
            />
          </div>
          <div className="filter_checks">
            <Inputs.InputCheckBox
              theme_standard_input_check_box
              label="เฉพาะที่จะถูกตัด"
              value="only_expiring"
              checked={onlyExpiring}
              onChange={() => this.handleToggle('onlyExpiring')}
            />
            <Inputs.InputCheckBox
              theme_standard_input_check_box
              label="เฉพาะยอดไม่ตรง"
              value="mismatch_only"
              checked={mismatchOnly}
              onChange={() => this.handleToggle('mismatchOnly')}
            />
          </div>
          <div className="btn_action_filter">
            <Buttons.BgStandard label="ค้นหา" onClick={this.fetchPreview} />
          </div>
          <div className="btn_action_filter">
            <Buttons.BgStandard
              theme_gray
              label="ล้างค่า"
              onClick={this.handleClear}
            />
          </div>
          <div className="btn_action_filter">
            <Buttons.BgStandard
              theme_export_btn
              label="Export"
              onClick={this.handleExport}
            />
          </div>
        </div>

        {isLoading ? (
          <Displays.Loading theme_standard_loading heightLayout="470px" />
        ) : !data ? (
          <div className="empty">โหลดข้อมูลไม่สำเร็จ</div>
        ) : (
          <>
            {periodMeta && (
              <div className="period_card">
                <div className="period_main">
                  <div className="period_key">ล็อต {periodMeta.key}</div>
                  <div className="period_line">
                    แต้มที่ได้รับช่วง <b>{periodMeta.received_label}</b>
                  </div>
                  <div className="period_line">
                    หมดอายุสิ้นวัน <b>{periodMeta.expire_label}</b>
                  </div>
                </div>
                <div className="period_side">
                  <span
                    className={`state_badge ${
                      periodMeta.done_at || periodMeta.last_run_status === 'success'
                        ? 'state_done'
                        : periodMeta.is_due
                        ? 'state_due'
                        : 'state_wait'
                    }`}
                  >
                    {RENDER_PERIOD_STATE(periodMeta)}
                  </span>
                  {periodMeta.run_count > 0 && (
                    <div className="period_meta">
                      รันแล้ว {periodMeta.run_count} รอบ
                      {periodMeta.done_at
                        ? ` • ตัดสำเร็จเมื่อ ${formatDateTime(periodMeta.done_at)}`
                        : ''}
                    </div>
                  )}
                  <div className="period_meta">
                    คำนวณ ณ {formatDateTime(data.now)}
                  </div>
                </div>
              </div>
            )}

            {/* summary.is_filtered = true เมื่อกรอง Login ID/CU → ตัวเลขสรุปเป็นของชุดที่กรองเท่านั้น */}
            {summary.is_filtered && (
              <div className="result_head">
                ตัวเลขสรุปด้านล่างนับเฉพาะลูกค้าที่ตรงกับ Login ID / CU code ที่กรอง
              </div>
            )}
            <div className="summary_row">
              {this.renderSummaryCard(
                `ลูกค้าทั้งหมด${summary.is_filtered ? ' (เฉพาะที่กรอง)' : ''}`,
                formatPoint(summary.customer_count)
              )}
              {this.renderSummaryCard(
                'ลูกค้าที่จะถูกตัด',
                formatPoint(summary.expiring_customer_count),
                'card_primary'
              )}
              {this.renderSummaryCard(
                'แต้มที่จะถูกตัดรวม',
                formatPoint(summary.total_point),
                'card_primary'
              )}
              {this.renderSummaryCard(
                'ยอดไม่ตรง',
                formatPoint(summary.mismatch_count),
                Number(summary.mismatch_count) > 0 ? 'card_danger' : ''
              )}
              {this.renderSummaryCard(
                'ไม่มี wallet',
                formatPoint(summary.no_wallet_count),
                Number(summary.no_wallet_count) > 0 ? 'card_warn' : ''
              )}
            </div>

            <div className="result_head">
              ผลลัพธ์ {formatPoint(docs.length)} ราย
              {docs.length > perPage ? ` (หน้า ${page}/${totalPages})` : ''}
            </div>
            <Tables.Standard
              columns={columns}
              data={pageDocs}
              onRowClick={this.handleClickBreakdown}
              showPagination
              pagination={{ last_page: totalPages, current_page: page }}
              handlePageClick={this.handlePageClick}
            />
          </>
        )}
        <Modals.BodyEmpty
          isShowModal={renderModal}
          onCloseModal={this.handleCloseModal}
        >
          {renderModal}
        </Modals.BodyEmpty>
      </PointExpirePreviewContainerStyled>
    );
  }
}

const numberCell = (props) => (
  <div style={{ textAlign: 'right' }}>{formatPoint(props.value)}</div>
);

// คลิกที่แถวเพื่อดูที่มาที่ไป (ไม่มีคอลัมน์ Action)
const columns = [
  {
    Header: <div style={{ textAlign: 'left' }}>Login ID</div>,
    accessor: 'customer_user_id',
    Cell: (props) => (
      <div>
        {props.value || '-'}
        {(props.row.original.firstname || props.row.original.lastname) && (
          <div className="sub_text">
            {`${props.row.original.firstname || ''} ${
              props.row.original.lastname || ''
            }`.trim()}
          </div>
        )}
      </div>
    ),
  },
  {
    Header: <div style={{ textAlign: 'left' }}>CU code</div>,
    accessor: 'customer_cu_id',
    Cell: (props) => props.value || '-',
  },
  {
    Header: <div style={{ textAlign: 'right' }}>ได้รับในล็อต</div>,
    accessor: 'lot_received',
    Cell: numberCell,
  },
  {
    Header: <div style={{ textAlign: 'right' }}>ใช้ไปจากล็อต</div>,
    accessor: 'lot_redeemed',
    Cell: numberCell,
  },
  {
    Header: <div style={{ textAlign: 'right' }}>คงเหลือในล็อต</div>,
    accessor: 'lot_remaining',
    Cell: numberCell,
  },
  {
    Header: <div style={{ textAlign: 'right' }}>ยอดใน wallet</div>,
    accessor: 'wallet_point',
    Cell: numberCell,
  },
  {
    Header: <div style={{ textAlign: 'right' }}>ยอดที่จะตัด</div>,
    accessor: 'expired_point',
    Cell: (props) => (
      <div
        style={{ textAlign: 'right' }}
        className={Number(props.value) > 0 ? 'expire_value' : ''}
      >
        {formatPoint(props.value)}
      </div>
    ),
  },
  {
    Header: <div style={{ textAlign: 'center' }}>ยอดไม่ตรง</div>,
    accessor: 'is_mismatch',
    Cell: (props) => (
      <div style={{ textAlign: 'center' }}>
        {props.value ? (
          <span
            className="flag_mismatch"
            title={props.row.original.mismatch_reason || ''}
          >
            <MdWarningAmber size={14} />
            ไม่ตรง
          </span>
        ) : (
          '-'
        )}
      </div>
    ),
  },
  {
    Header: <div style={{ textAlign: 'left' }}>สาเหตุ</div>,
    accessor: 'mismatch_reason',
    Cell: (props) =>
      props.value ? (
        <div className="reason_text" title={props.value}>
          {props.value}
        </div>
      ) : (
        '-'
      ),
  },
];

export default withRouter(PointExpirePreviewContainer);
